/**
 * Pure helpers over the help-centre article set.
 *
 * Deliberately separate from `index.ts`: that module uses `import.meta.glob`, which Jest
 * cannot parse, so everything testable lives here as pure functions taking the article
 * array as input. Pages compose them as `f(visibleArticles(articles, isPatientPortalUser()), …)`.
 *
 * ⚠️ Audience is a VISIBILITY rule, not just a listing rule. Every function here expects
 * the caller to pass the already-filtered set (from `visibleArticles`) so that search
 * results, related links and prev/next can never point a patient at a practitioner guide.
 */

import type { CategoryDef } from "./categories";
import type { DocArticle, DocCategory } from "./types";

/**
 * The one-point audience switch (ClickUp 86d3zt9d3 decision): staff see everything,
 * patients see only guides written for them (`audience: patient` — the whole
 * `patient-portal` category, per the importer's category→audience mapping).
 */
export function visibleArticles(all: DocArticle[], viewerIsPatient: boolean): DocArticle[] {
  return viewerIsPatient ? all.filter((a) => a.audience === "patient") : all;
}

/** Categories that actually have visible content, in display order. */
export function buildCategories(articles: DocArticle[], defs: CategoryDef[]): DocCategory[] {
  return defs
    .map((def) => ({ ...def, articles: articles.filter((a) => a.category === def.slug) }))
    .filter((c) => c.articles.length > 0)
    .sort((a, b) => a.order - b.order);
}

export function findArticle(
  articles: DocArticle[],
  category: string,
  slug: string,
): DocArticle | undefined {
  return articles.find((a) => a.category === category && a.slug === slug);
}

/** Resolve `related:` slugs against the visible set — dangling slugs simply drop out. */
export function findRelated(articles: DocArticle[], article: DocArticle): DocArticle[] {
  return article.related
    .map((slug) => articles.find((a) => a.slug === slug))
    .filter((a): a is DocArticle => Boolean(a));
}

/** Previous/next within the same category, following the array's existing sort order. */
export function siblingArticles(
  articles: DocArticle[],
  article: DocArticle,
): { prev?: DocArticle; next?: DocArticle } {
  const group = articles.filter((a) => a.category === article.category);
  const i = group.findIndex((a) => a.slug === article.slug);
  if (i === -1) return {};
  return { prev: group[i - 1], next: group[i + 1] };
}

/**
 * Case-insensitive substring match over title, summary and section headings.
 * An empty/whitespace query returns the full set — the guides tab treats "no query"
 * as "browse everything".
 */
export function searchArticles(articles: DocArticle[], query: string): DocArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return articles;
  return articles.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.headings.some((h) => h.text.toLowerCase().includes(q)),
  );
}
